export function inferPublicSafetyCaseIdFromFilename(fileName: string): string | null {
  const nameWithoutExtension = fileName.replace(/\.[^.]+$/, '');
  const yearAndCasePattern = new RegExp(
    String.raw`(?:^|[^A-Za-z0-9])` +
      String.raw`((?:(?:19|20)\d{2}|\d{2})(?:[\s._-]+[A-Za-z]{2,}){0,2}[\s._-]+\d{3,})` +
      String.raw`(?=$|[^A-Za-z0-9])`,
    'g'
  );
  const matches = [...nameWithoutExtension.matchAll(yearAndCasePattern)]
    .map(match => match[1]?.trim())
    .filter(Boolean);

  if (matches.length > 0)
    return matches[0] ?? null;

  const numericTokens = nameWithoutExtension.match(/\d{5,}/g) ?? [];
  return numericTokens.sort((a, b) => b.length - a.length)[0] ?? null;
}

export type PublicSafetyOfficerIdentity = {
  name: string | null;
  title: string | null;
};

const OFFICER_TITLE_PATTERN = /^(?:officer|ofc|constable|cst|detective|det|sergeant|sgt|corporal|cpl|lieutenant|lt|captain|cpt)\.?$/i;
const OFFICER_NAME_STOP_TOKENS = new Set([
  'bodycam',
  'body',
  'camera',
  'case',
  'caseid',
  'clip',
  'final',
  'id',
  'interview',
  'name',
  'recording',
  'report',
  'title',
  'upload',
  'video',
  'worn',
]);

const OFFICER_TITLE_PREFIX_PATTERN = /^(officer|ofc|constable|cst|detective|det|sergeant|sgt|corporal|cpl|lieutenant|lt|captain|cpt)\.?(.+)$/i;

function normalizeToken(value: string): string {
  return value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function titleCase(value: string): string {
  return normalizeToken(value)
    .split(' ')
    .filter(Boolean)
    .map(part => {
      if (/^[A-Z]{2,}$/.test(part)) return part;
      return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
    })
    .join(' ');
}

function normalizeOfficerTitle(value: string): string {
  const normalized = normalizeToken(value).replace(/\.$/, '').toLowerCase();
  switch (normalized) {
    case 'ofc':
      return 'Officer';
    case 'cst':
      return 'Constable';
    case 'det':
      return 'Detective';
    case 'sgt':
      return 'Sergeant';
    case 'cpl':
      return 'Corporal';
    case 'lt':
      return 'Lieutenant';
    case 'cpt':
      return 'Captain';
    default:
      return titleCase(normalized);
  }
}

function tokenizeOfficerFilename(fileName: string): string[] {
  const nameWithoutExtension = fileName.replace(/\.[^.]+$/, '');
  const rawTokens = nameWithoutExtension
    .split(/[\s._-]+/)
    .map(token => token.trim())
    .filter(Boolean);

  return rawTokens.flatMap(token => {
    if (OFFICER_TITLE_PATTERN.test(token)) return [token];

    const prefixedTitle = token.match(OFFICER_TITLE_PREFIX_PATTERN);
    if (!prefixedTitle) return [token];

    const [, title, name] = prefixedTitle;
    return name ? [title, name] : [token];
  });
}

export function inferPublicSafetyOfficerIdentityFromFilename(fileName: string): PublicSafetyOfficerIdentity {
  const tokens = tokenizeOfficerFilename(fileName);
  const titleIndex = tokens.findIndex(token => OFFICER_TITLE_PATTERN.test(token));

  if (titleIndex < 0) {
    return { name: null, title: null };
  }

  const title = normalizeOfficerTitle(tokens[titleIndex]);
  const nameTokens = tokens
    .slice(titleIndex + 1, titleIndex + 4)
    .filter(token =>
      !OFFICER_TITLE_PATTERN.test(token)
      && !/^\d+$/.test(token)
      && !OFFICER_NAME_STOP_TOKENS.has(token.toLowerCase())
      && (token.length > 1 || /^[A-Z]$/i.test(token))
    );

  if (nameTokens.length === 0) {
    return { name: null, title: null };
  }

  return {
    title,
    name: titleCase(nameTokens.join(' ')),
  };
}
